/** @file Lawson edge flips that turn a polygon triangulation into its constrained Delaunay triangulation. */
import { flatOrient } from './flatOrient';
import { inCircle } from './inCircle';
import { pointsScale } from './pointsScale';

interface FlipCtx {
  p: ArrayLike<number>;
  tris: number[];
  hullCount: number;
  /** Vertex count: directed edge q→r is keyed q * n + r. */
  n: number;
  tol: number;
}

function isHullEdge(a: number, b: number, hullCount: number): boolean {
  if (a >= hullCount || b >= hullCount) return false;
  return (a + 1) % hullCount === b || (b + 1) % hullCount === a;
}

/** [corner at position i, the next corner, the third corner] of the triangle holding position i. */
function cornersFrom(tris: number[], i: number): [number, number, number] {
  const t = i - (i % 3);
  return [tris[i] ?? 0, tris[t + ((i + 1) % 3)] ?? 0, tris[t + ((i + 2) % 3)] ?? 0];
}

/** Directed edge key → position (into the flat triangle array) of the corner the edge starts at. */
function edgeOwners(ctx: FlipCtx): Map<number, number> {
  const owners = new Map<number, number>();
  for (let i = 0; i < ctx.tris.length; i++) {
    const [q, r] = cornersFrom(ctx.tris, i);
    owners.set(q * ctx.n + r, i);
  }
  return owners;
}

/** Flips edge q→r (starting at position i, third corner a) when the far corner s is inside circle(a, q, r) and aqsr is convex. */
function tryFlip(ctx: FlipCtx, owners: Map<number, number>, i: number): boolean {
  const [q, r, a] = cornersFrom(ctx.tris, i);
  const j = owners.get(r * ctx.n + q);
  if (q > r || j === undefined || isHullEdge(q, r, ctx.hullCount)) return false;
  const s = cornersFrom(ctx.tris, j)[2];
  if (inCircle(ctx.p, a, q, r, s) <= ctx.tol) return false;
  if (flatOrient(ctx.p, a, q, s) <= 0 || flatOrient(ctx.p, a, s, r) <= 0) return false;
  ctx.tris.splice(i - (i % 3), 3, a, q, s);
  ctx.tris.splice(j - (j % 3), 3, a, s, r);
  return true;
}

/** Flips non-hull edges until each is locally Delaunay (cocircular quads stay); mutates and returns `triangles`. */
export function delaunayFlips(p: ArrayLike<number>, triangles: number[], hullCount: number): number[] {
  const ctx: FlipCtx = { p, tris: triangles, hullCount, n: p.length / 2, tol: 1e-10 * pointsScale(p) ** 4 };
  const maxPasses = 50 * (triangles.length / 3) + 100;
  for (let pass = 0; pass < maxPasses; pass++) {
    const owners = edgeOwners(ctx);
    let flipped = false;
    for (let i = 0; i < triangles.length && !flipped; i++) flipped = tryFlip(ctx, owners, i);
    if (!flipped) break;
  }
  return triangles;
}
